import { Flex } from '@chakra-ui/react';
import { Tag, Text } from '@kula-ai/components';
import { fillIdPlaceholders } from '../../../helpers';
import { useChatSubmit } from '../../../hooks';
import type { ActionResultData } from '../../../types';
import { SuggestedActionChips } from '../SuggestedActionChips';

interface ActionResultBlockProps {
  data: ActionResultData;
}

// Generic fallback card for structured results with no dedicated renderer
// (unknown search entities, misc action outcomes). Previously these payloads
// fell through to `actionResult` which had NO registered component, so the
// user saw blank space where a result should be. A summary line + entity
// chips + follow-up chips keeps every backend payload visible.
export const ActionResultBlock = ({ data }: ActionResultBlockProps) => {
  const { send, isSending } = useChatSubmit();
  if (!data.summary && !data.entities?.length) return null;

  const ids = data.entities?.map((e) => e.id) ?? [];
  const sendFollowup = (template: string) => {
    if (isSending) return;
    void send(fillIdPlaceholders(template, ids));
  };

  return (
    <Flex direction="column" gap="8px" maxW="520px">
      {data.summary && (
        <Text variant="small" className="text-secondary-500">
          {data.summary}
        </Text>
      )}
      {data.entities && data.entities.length > 0 && (
        <Flex wrap="wrap" gap="6px">
          {data.entities.slice(0, 20).map((e) => (
            <Tag
              key={`${e.kind}-${e.id}`}
              label={e.label}
              size="md"
              variant="outline"
              data-test-id={`action-result-entity-${e.id}`}
            />
          ))}
        </Flex>
      )}
      {data.suggestedActions && data.suggestedActions.length > 0 && (
        <SuggestedActionChips
          actions={data.suggestedActions}
          onSelect={sendFollowup}
          disabled={isSending}
          testIdPrefix="action-result-suggested"
        />
      )}
    </Flex>
  );
};
